// frontend/src/lib/sentry.ts
//
// SPA-side Sentry encapsulation. Mirrors internal/sentry/ on the Go side:
// only this file imports @sentry/react; everything else uses the helpers
// exported here.
//
// Soft-disable contract: when config.sentryDSN is empty (typical in dev),
// init() is a no-op and every helper exits early. The feedback feature
// still works end-to-end; the GitHub issue body's sentry.* keys just
// ship empty.
//
// PII discipline (matches the Go beforeSend in internal/sentry/sentry.go):
//   - We never send name / email / IP. The opaque submitter handle is the
//     only User attribute. The handle is the same value the GitHub issue
//     footer carries, so a triager can pivot from issue → Sentry events
//     without leaking emails.
//   - beforeSend scrubs JWTs, password=… query/body fragments, and Swedish
//     personnummer. Coarse regex; false positives over false negatives.
//   - replaysOnErrorSampleRate = 1.0 captures a replay only when an error
//     is captured. replaysSessionSampleRate stays 0 — we never record
//     normal sessions; only the moments around exceptions.
//   - data-sentry-unmask is opt-in per element; default behaviour is to
//     mask all input + text content. Keep label / nav text unmasked via
//     the prop on those primitives.

import * as Sentry from '@sentry/react';

import { config } from '../config';

let initialised = false;

export function initSentry(): void {
  if (initialised) return;
  initialised = true;

  if (!config.sentryDSN) return;

  const initOpts: Parameters<typeof Sentry.init>[0] = {
    dsn: config.sentryDSN,
    environment: config.sentryEnvironment,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        // Block known-sensitive elements outright (network logs / debug
        // panels in dev; never expected in prod).
        blockAllMedia: true,
      }),
    ],
    // Sample replays only when the user hits an error. Normal sessions
    // are not recorded — privacy-by-default.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Browser-side scrub layer. Mirrors internal/sentry's beforeSend
    // (Authorization header, password=…, Swedish personnummer, JWT).
    beforeSend(event) {
      if (event.request) {
        const req = event.request as Record<string, unknown>;
        const headers = req['headers'];
        if (headers && typeof headers === 'object') {
          delete (headers as Record<string, string>)['Authorization'];
          delete (headers as Record<string, string>)['authorization'];
          delete (headers as Record<string, string>)['Cookie'];
          delete (headers as Record<string, string>)['cookie'];
        }
        if (req['cookies']) req['cookies'] = '[redacted]';
        if (typeof req['query_string'] === 'string') {
          req['query_string'] = redactQuery(req['query_string'] as string);
        }
        if (req['data']) req['data'] = '[redacted]';
      }
      if (event.exception?.values) {
        for (const ex of event.exception.values) {
          if (typeof ex.value === 'string') ex.value = redactString(ex.value);
        }
      }
      if (event.breadcrumbs) {
        for (const b of event.breadcrumbs) {
          if (typeof b.message === 'string') b.message = redactString(b.message);
        }
      }
      if (event.user) {
        // Drop everything except the opaque handle in `id`.
        const id = event.user.id;
        event.user = id ? { id } : {};
      }
      return event;
    },
  };
  if (config.sentryRelease) initOpts.release = config.sentryRelease;
  Sentry.init(initOpts);
}

// setUser stamps the opaque submitter handle on the per-tab Sentry scope.
// Called once at login/onboarding finish, and again whenever the user
// switches orgs (the handle is per-user, not per-org, so this is mostly
// idempotent — but a re-call is cheap and keeps the contract obvious).
export function setUser(opaqueHandle: string): void {
  if (!config.sentryDSN || !opaqueHandle) return;
  Sentry.setUser({ id: opaqueHandle });
}

// addBreadcrumb is a thin wrapper kept here so production code never has
// to reach for the SDK directly. Useful for surfacing CommandIntent
// approvals + form submits in the breadcrumb trail.
export function addBreadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
  if (!config.sentryDSN) return;
  const bc: Parameters<typeof Sentry.addBreadcrumb>[0] = { category, message };
  if (data) bc.data = data;
  Sentry.addBreadcrumb(bc);
}

// startReplay is exported for the feedback form: when the form mounts
// we deliberately enable replay so a hard reload + bundle pull captures
// the user's last few seconds. (Without this, replays only fire on
// uncaught exceptions; the feedback form is precisely the moment where
// the user is *describing* a problem they noticed.)
export function startReplay(): void {
  if (!config.sentryDSN) return;
  const replay = Sentry.getReplay();
  if (replay && typeof replay.start === 'function') {
    try {
      replay.start();
    } catch {
      // The SDK throws if the replay is already running; harmless.
    }
  }
}

// captureException is the typed escape hatch for code paths that catch
// errors before they propagate. Most code should just let the React
// error boundary handle it; this is for places where we'd otherwise
// `console.error` and lose the trail.
export function captureException(err: unknown): string | null {
  if (!config.sentryDSN) return null;
  const id = Sentry.captureException(err);
  return typeof id === 'string' ? id : null;
}

// --- Redaction helpers shared with the test suite. ---

const JWT_RE = /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g;
const PASSWORD_RE = /(password|passwd)=[^&\s]+/gi;
const PNO_RE = /\b(?:19|20)?\d{6}[-+]\d{4}\b/g;

export function redactString(s: string): string {
  if (!s) return s;
  return s
    .replace(JWT_RE, '[redacted-jwt]')
    .replace(PASSWORD_RE, 'password=[redacted]')
    .replace(PNO_RE, '[redacted-pno]');
}

export function redactQuery(q: string): string {
  if (!q) return q;
  const params = new URLSearchParams(q);
  for (const k of Array.from(params.keys())) {
    if (['password', 'token', 'secret', 'api_key', 'apikey'].includes(k.toLowerCase())) {
      params.set(k, '[redacted]');
    }
  }
  return params.toString();
}
